// ═══════════════════════════════════════════════════════════════════════════
// grainCashFlow.js — "what cash lands in which month, from the sales already
// made?" (Kevin, 2026-08-20: "a real simple cash flow vertical bar graph to
// show the farmers cash flow by month based on their sales").
//
// ── THREE KINDS OF COMMITTED BUSHEL, AND THEY ARE NOT ONE NUMBER ──────────
// A contract commits bushels; it does not always fix a dollar. Meisenzahl's
// CY2026 corn is the whole problem in one book:
//
//   Fixed Price   5,000 bu @ $5.07 cash          -> the money IS known
//   HTA          30,000 bu, futures locked        -> futures fixed, BASIS OPEN.
//                4.54-5.02, basis 0/absent           The cash is futures plus a
//                                                    basis nobody has set yet.
//   Bushels Only  5,000 bu, no price at all       -> bushels owed, no dollars
//
// Rendering one bar per month with one total would state a number for 35,000
// bushels whose price does not exist yet. So the three states stay SEPARATE all
// the way to the chart:
//
//   known      cash_price is set -> bushels x cash_price. A fact.
//   estimated  futures_price set, basis not -> bushels x (futures + basisRef),
//              and ONLY when the caller supplies a basisRef it can source. No
//              basisRef, no estimate — the bushels fall to `unpricedBu`
//              instead of silently being valued at futures alone, which would
//              overstate every delivered dollar by the basis.
//   unpriced   neither -> bushels only, never a dollar.
//
// ── THE DELIVERY WINDOW IS A WINDOW ──────────────────────────────────────
// A contract says "Oct 1 - Dec 1", not "October". Putting the whole amount in
// the start month invents a delivery date; so does putting it in the end month.
// The bushels are spread EVENLY across the months the window touches and the
// chart says so. That is a stated assumption, not a hidden one — and it is the
// only part of this file that is not a direct reading of the contract.
//
// A contract with no delivery window at all cannot be placed on a month axis.
// It is counted into `undated` and reported, never dropped and never guessed
// onto the harvest month.
//
// PURE — no fetch, no React, no Supabase.
// ═══════════════════════════════════════════════════════════════════════════

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const monthKey = (y, m) => `${y}-${String(m + 1).padStart(2, "0")}`;
export const monthLabel = (key) => {
  const [y, m] = String(key || "").split("-").map(Number);
  return Number.isFinite(y) && Number.isFinite(m) ? `${MONTHS[m - 1]} ${String(y).slice(-2)}` : "";
};

function parseDate(v) {
  if (!v) return null;
  const s = String(v).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]) - 1 };
}

// Months a delivery window touches, inclusive. A window with no end is a single
// month — the contract named one date, so one month is what it states.
function windowMonths(startRaw, endRaw) {
  const a = parseDate(startRaw);
  if (!a) return [];
  const b = parseDate(endRaw) || a;
  const out = [];
  let y = a.y, m = a.m;
  for (let guard = 0; guard < 120; guard++) {
    out.push(monthKey(y, m));
    if (y > b.y || (y === b.y && m >= b.m)) break;
    m++; if (m > 11) { m = 0; y++; }
  }
  return out;
}

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : null; };

// ── buildMonthlyCashFlow ──────────────────────────────────────────────────
// contracts: grain_contracts rows. basisRef: a $/bu basis to value futures-only
// contracts at, or null. Supply it ONLY from an observed bid — passing a guess
// here turns every HTA bar into a fabricated number.
export function buildMonthlyCashFlow(contracts = [], { basisRef = null, basisRefLabel = null } = {}) {
  const months = new Map();
  const bucket = (k) => {
    if (!months.has(k)) months.set(k, { key: k, known: 0, estimated: 0, knownBu: 0, estimatedBu: 0, unpricedBu: 0 });
    return months.get(k);
  };
  let undatedBu = 0, undatedCount = 0;
  let usedBasisRef = false;

  for (const c of contracts || []) {
    // Bushels still owed to the buyer. `bushels_delivered` is what has already
    // gone; the cash-flow question is about what is still to come, so delivered
    // bushels are netted off rather than double-counted as future cash.
    const contracted = num(c.bushels_contracted) || 0;
    const delivered = num(c.bushels_delivered) || 0;
    const remaining = Math.max(0, contracted - delivered);
    if (remaining <= 0) continue;

    const cash = num(c.cash_price);
    const fut = num(c.futures_price);
    const basis = num(c.basis);

    // A stored 0 is not a price. Every HTA in the book carries cash_price 0 and
    // basis 0 meaning "not set"; treating that as $0.00/bu would report the
    // farm delivering 30,000 bushels for nothing.
    const cashSet = cash != null && cash > 0;
    const futSet = fut != null && fut > 0;
    const basisSet = basis != null && basis !== 0;

    const mkeys = windowMonths(c.delivery_start, c.delivery_end);
    if (!mkeys.length) { undatedBu += remaining; undatedCount++; continue; }
    const share = remaining / mkeys.length;

    for (const k of mkeys) {
      const b = bucket(k);
      if (cashSet) {
        b.known += share * cash; b.knownBu += share;
      } else if (futSet && basisSet) {
        // Futures and an explicit contract basis: the cash IS determined, just
        // recorded in two fields rather than one.
        b.known += share * (fut + basis); b.knownBu += share;
      } else if (futSet && basisRef != null) {
        b.estimated += share * (fut + basisRef); b.estimatedBu += share; usedBasisRef = true;
      } else {
        b.unpricedBu += share;
      }
    }
  }

  const rows = [...months.values()].sort((a, b) => (a.key < b.key ? -1 : 1));
  return {
    months: rows.map((r) => ({ ...r, total: r.known + r.estimated, label: monthLabel(r.key) })),
    totalKnown: rows.reduce((s, r) => s + r.known, 0),
    totalEstimated: rows.reduce((s, r) => s + r.estimated, 0),
    unpricedBu: rows.reduce((s, r) => s + r.unpricedBu, 0),
    undatedBu, undatedCount,
    // Named so the chart can say WHICH basis every estimated bar leans on, and
    // so a reader can tell an estimate from a settled price at a glance.
    basisRef: usedBasisRef ? basisRef : null,
    basisRefLabel: usedBasisRef ? basisRefLabel : null,
  };
}
